import { IGraphProvider, IComparatorApiServiceParameters } from '../..';
import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  NormalizedCacheObject,
  FetchPolicy,
  HttpLink,
} from '@apollo/client';
import { persistCacheSync, LocalStorageWrapper } from 'apollo3-cache-persist';
import { setContext } from '@apollo/client/link/context';
import moment from 'moment';
import { MutationFetchPolicy } from '@apollo/client/core/watchQueryOptions';
export class ApolloProvider implements IGraphProvider {
  private apolloClient: ApolloClient<NormalizedCacheObject>;
  private baseUrl;
  private tokenUrl;
  private credentials;

  constructor(params: IComparatorApiServiceParameters) {
    this.baseUrl = params.baseUrl;
    this.tokenUrl = params.tokenUrl;
    this.credentials = params.credentials;
    // Initializing apollo client
    const cache = new InMemoryCache();
    const httpLink = createHttpLink({
      uri: params.baseUrl,
    });
    const authLink = this.getAuthLink();
    this.apolloClient = new ApolloClient({
      link: authLink.concat(httpLink),
      cache,
    });

    persistCacheSync({
      cache,
      storage: new LocalStorageWrapper(window.localStorage),
    });
  }

  private getAuthLink() {
    const authLink = setContext(async (_, { headers }) => {
      if (
        this.credentials.token === '' ||
        moment().isAfter(this.credentials.expiresIn)
      ) {
        await this.updateCredentials();
      }

      return {
        headers: {
          ...headers,
          authorization: this.credentials.token
            ? `Bearer ${this.credentials.token}`
            : '',
        },
      };
    });

    return authLink;
  }

  private async acquireToken(): Promise<Response> {
    const scope = process.env.KIZI_PUBLIC_API_SCOPE;
    let body = 'client_id=' +
      this.credentials.clientId +
      '&client_secret=' +
      this.credentials.clientSecret +
      '&grant_type=client_credentials';

    if (scope) {
      body += "&scope=" + scope;
    }

    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body
    };

    if (this.tokenUrl && this.tokenUrl.length > 0) {
      return fetch(this.tokenUrl, requestOptions);
    } else {
      return Promise.reject('Invalid Token url');
    }
  }

  private async updateCredentials() {
    const response = await this.acquireToken();
    const payload = response.ok && response.json ? await response.json() : '';
    this.credentials.token = payload.access_token;
    this.credentials.expiresIn = moment().add(
      payload.expires_in - 1,
      'seconds'
    );
    localStorage.setItem('accessToken', payload.access_token);
  }

  public async query<T>(
    query: any,
    variables: object,
    dataName: string,
    fetchPolicy?: FetchPolicy,
    newClientLink?: string
  ): Promise<T> {
    if (newClientLink) {
      const httpLink = new HttpLink({
        uri: newClientLink,
      });
      const authLink = this.getAuthLink();
      this.apolloClient.setLink(authLink.concat(httpLink));
    }

    if (
      this.credentials.token === '' ||
      moment().isAfter(this.credentials.expiresIn)
    ) {
      await this.updateCredentials();
    }

    return new Promise<T>((resolve, reject) => {
      this.apolloClient
        .query({ query, variables, fetchPolicy })
        .then(({ data, errors }) => {
          if (newClientLink) {
            const resetLink = new HttpLink({
              uri: this.baseUrl,
            });
            const authLink = this.getAuthLink();
            this.apolloClient.setLink(authLink.concat(resetLink));
          }
          errors && reject(errors);
          if (data[dataName]) {
            resolve(data[dataName]);
          } else {
            reject('dataName was not found');
          }
        })
        .catch((status) => {
          if ([401].indexOf(status) != -1) {
            //token expired probably
            this.credentials.token = '';
            return this.query<T>(
              query,
              variables,
              dataName,
              fetchPolicy,
              newClientLink
            )
              .then((data) => {
                resolve(data);
              })
              .catch((error) => {
                reject(error);
              });
          }

          return Promise.reject(status);
        })
        .catch((error) => {
          reject(error);
        }); //this is needed so we do not get warning for unhandled rejection
    });
  }

  public async mutate<T>(
    mutation: any,
    variables: object,
    dataName: string,
    fetchPolicy?: MutationFetchPolicy,
    newClientLink?: string
  ): Promise<T> {
    if (newClientLink) {
      const httpLink = new HttpLink({
        uri: newClientLink,
      });
      const authLink = this.getAuthLink();
      this.apolloClient.setLink(authLink.concat(httpLink));
    }

    if (
      this.credentials.token === '' ||
      moment().isAfter(this.credentials.expiresIn)
    ) {
      await this.updateCredentials();
    }

    return new Promise<T>((resolve, reject) => {
      this.apolloClient
        .mutate({ mutation, variables, fetchPolicy })
        .then(({ data }) => {
          if (newClientLink) {
            const resetLink = new HttpLink({
              uri: this.baseUrl,
            });
            const authLink = this.getAuthLink();
            this.apolloClient.setLink(authLink.concat(resetLink));
          }
          if (data[dataName]) {
            resolve(data[dataName]);
          } else {
            reject('dataName was not found');
          }
        })
        .catch((status) => {
          if ([401].indexOf(status) != -1) {
            //token expired probably
            this.credentials.token = '';
            return this.mutate<T>(
              mutation,
              variables,
              dataName,
              fetchPolicy,
              newClientLink
            )
              .then((data) => {
                resolve(data);
              })
              .catch((error) => {
                reject(error);
              });
          }

          return Promise.reject(status);
        })
        .catch((error) => {
          reject(error);
        }); //this is needed so we do not get warning for unhandled rejection
    });
  }
}
